import React, { useEffect, useState, MouseEvent } from "react";

/* -------------------------------------------------------------------------
 * DownloadOptionsPopup
 * -------------------------------------------------------------------------
 * A reusable modal component that presents download options for chart(s).
 * -------------------------------------------------------------------------
 * Props
 * -----
 * isOpen       : Whether the modal is visible.
 * chartName    : The name of the chart being downloaded (used in labels).
 * onClose      : Callback for closing the modal.
 * onDownload   : Callback invoked with the selected option ("single" | "all").
 * ------------------------------------------------------------------------- */

export interface DownloadOptionsPopupProps {
  isOpen: boolean;
  chartName: string;
  onClose: () => void;
  onDownload: (option: "single" | "all") => void;
  /** Optional URL for the user avatar image. */
  avatarSrc?: string;
}

export const DownloadOptionsPopup: React.FC<DownloadOptionsPopupProps> = ({
  isOpen,
  chartName,
  onClose,
  onDownload,
}) => {
  const [selectedOption, setSelectedOption] = useState<"single" | "all">(
    "single"
  );

  /* ---------------------------------------------------------------------
   * Side-effects
   * ------------------------------------------------------------------- */
  // Lock background scrolling while modal is open, unlock when closed.
  useEffect(() => {
    if (!isOpen) return; // No lock needed if modal is closed.

    const { overflow: originalOverflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  /* ---------------------------------------------------------------------
   * Handlers
   * ------------------------------------------------------------------- */
  const handleBackdropClick = () => {
    onClose();
  };

  const handleContainerClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent backdrop click when interacting inside modal.
  };

  const handleDownload = () => {
    onDownload(selectedOption);
  };

  /* ---------------------------------------------------------------------
   * JSX
   * ------------------------------------------------------------------- */
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 backdrop-filter backdrop-grayscale"
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-[600px] rounded-lg bg-white p-10 shadow-[0_4px_24px_rgba(0,0,0,0.15)] pt-8"
        onClick={handleContainerClick}
      >
        {/* Top-right controls */}
        <div className="absolute top-6 right-8 flex items-center gap-4">
          <button
            aria-label="Close"
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 text-[#212121] transition-colors hover:text-gray-900"
          >
            <span className="material-symbols-outlined text-[30px] leading-none pointer-events-none font-light">
              close
            </span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-5 flex items-start gap-2 flex-wrap">
          <span className="material-symbols-outlined text-[30px] text-[#212121] leading-none w-8 h-8 flex items-center justify-center font-light">
            download
          </span>
          <h2 className="text-2xl text-[#212121] font-normal font-geist break-words w-[80%]" style={{ lineHeight: '1.3', fontSize: '24px', fontWeight: '550'}}>
            Download {chartName} Chart
          </h2>
        </div>

        {/* Description */}
        <p className="mb-8 text-sm leading-tight text-[#212121] font-geist" style={{ lineHeight: '1.2', fontSize: '16px', fontWeight: '400' }}>
          Download our charts to easily use in reports or presentations! You have the option of downloading just this chart or all of them at once. 
        </p>
        {/* I want to make this use font weight 500 and line spacing 1.3 */}
        <p className="mb-6 text-sm leading-relaxed text-[#212121] font-inter" style={{ lineHeight: '1.3', fontSize: '16px', fontWeight: '500' }}>Please select an option below: </p>

        {/* Radio options */}
        <div role="radiogroup">
          <label className="mb-4 flex cursor-pointer items-center gap-3">
            <input
              type="radio"
              name="download-option"
              value="single"
              className="h-5 w-5 accent-[#CA0079]"
              checked={selectedOption === "single"}
              onChange={() => setSelectedOption("single")}
            />
            <span className="text-[#212121] font-inter font-light" style={{ lineHeight: '1.3', fontSize: '16px', fontWeight: '400' }}>
              Download {chartName} (PNG)
            </span>
          </label>

          <label className="flex cursor-pointer items-center gap-3 mb-3">
            <input
              type="radio"
              name="download-option"
              value="all"
              className="h-5 w-5 accent-[#CA0079]"
              checked={selectedOption === "all"}
              onChange={() => setSelectedOption("all")}
            />
            <span className="text-[#212121] font-inter font-light" style={{ lineHeight: '1.3', fontSize: '16px', fontWeight: '400' }}>
              Download All Charts (PNG)
            </span>
          </label>
        </div>

        {/* Download button */}
        <button
          className="mt-6 h-9 w-full rounded-md bg-[#CA0079] text-white transition-colors hover:bg-[#A6006B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#CA0079] font-inter"
          onClick={handleDownload}
        >
          Download
        </button>
      </div>
    </div>
  );
};

// Provide a display name for better React DevTools experience.
DownloadOptionsPopup.displayName = "DownloadOptionsPopup"; 